import { PaymentAttempt } from '../models/entities/payment-attempt';
import { PaymentItem } from '../models/entities/payment-item';
import { Transaction } from '../models/entities/transactions/transaction';

/**
 * Transaction amount color convention (SD-7605).
 *
 * The legfiBalance pipe colors purely by the SIGN of the value it receives:
 * negative -> green (money in), positive/zero -> black (money out). PayHOA's stored
 * convention matches: `originalAmount` is negative for money-in (revenue) and positive
 * for money-out (expense). So the display amount for ANY transaction row — parent or
 * child — is driven by `originalAmount`, with ONE documented exception:
 *
 *   Return / dispute / refund lines are always money-out regardless of stored sign.
 *   A reversal is encoded in `deletedAt`, a 'Returned:'/'Disputed:' description, or the
 *   PaymentAttempt.returned flag — NOT in the amount sign (a soft-deleted refund keeps
 *   its original inflow sign). See isReturnLine.
 *
 * Transfers are a separate, deliberate neutral concern handled by the pipe's `forceNeutral`
 * argument at the call site — never folded into the sign logic here.
 */

/**
 * Signed display amount for a transaction row (parent or child). Its sign drives the
 * legfiBalance color: negative -> green (money in), positive -> black (money out).
 *
 * `recurseChildren` (default true) controls the all-children-return heuristic (SD-7465).
 * Top-level rows pass `false`: a deposit's own `originalAmount` sign is authoritative, so its
 * color must NOT depend on its children — otherwise, under the skeleton-load perf flag where
 * child rows backfill after the parent paints, an all-return deposit would render green then
 * flip to black (SD-7605). Payer-node child rows keep the default (true): their own sign is
 * unreliable, so they derive direction from their return leaves.
 */
export function getTransactionDisplayAmount(transaction: Transaction, recurseChildren = true): number {
    const magnitude = Math.abs(transaction.originalAmount ?? transaction.amount ?? 0);
    return isOutflow(transaction, recurseChildren) ? magnitude : -magnitude;
}

/**
 * Whether a row represents money out (renders black). Money-out when it carries a
 * return/dispute/refund marker (the documented exception, see isReturnLine), otherwise by
 * the authoritative sign convention: a positive `originalAmount` is an expense/outflow.
 */
function isOutflow(transaction: Transaction, recurseChildren = true): boolean {
    return isReturnLine(transaction, recurseChildren) || (transaction.originalAmount ?? 0) > 0;
}

/**
 * Whether a line carries a definite return/dispute/refund marker. Used both directly (a
 * leaf's own signal) and recursively to decide a parent node's direction.
 *
 * SD-7465: a payer/attempt node carries none of these per-line signals of its own, and its
 * PaymentAttempt.returned flag is an attempt-level, *shared* boolean that stays false for a
 * partial refund (it is only set once no live payment_items remain, and the same attempt is
 * also referenced by the still-live original payment). Such a node instead has child leaves
 * that DO carry the return signal ('Returned:'/'Disputed:'), so derive the node's direction
 * from its children when it has some and every one of them is itself a return. The length
 * guard matters: [].every() is true, so an unloaded/childless node must stay green.
 *
 * The amount sign is deliberately NOT consulted here: a bare negative (inflow-signed) amount
 * is not a reversal, so an all-adjustment/discount node has no real return leaves and stays
 * green. Sign-based money-direction is handled separately in isOutflow.
 *
 * `recurseChildren=false` (used for top-level rows) skips the all-children-return branch so a
 * parent colors by its own sign + own markers only — see getTransactionDisplayAmount.
 */
function isReturnLine(transaction: Transaction, recurseChildren = true): boolean {
    const description = (transaction.description || '').toLowerCase();

    return description.startsWith('returned') ||
           description.startsWith('disputed') ||
           !!transaction.deletedAt ||
           !!(transaction.reference && 'returned' in transaction.reference && (transaction.reference as PaymentAttempt).returned) ||
           (recurseChildren && !!transaction.children?.length && transaction.children.every(child => isReturnLine(child)));
}

/**
 * Signed display amount for a PaymentItem (deposit-detail dialog). A PaymentItem carries no
 * signed `originalAmount` — only a positive `amount` plus `deletedAt` — so it expresses the
 * SAME return exception (see the module docblock) using soft-delete alone: a live item is an
 * inflow (green), a soft-deleted one is a returned outflow (black).
 */
export function getPaymentItemDisplayAmount(item: PaymentItem): number {
    const isReturned = !!item.deletedAt;
    return isReturned ? Math.abs(item.amount) : -Math.abs(item.amount);
}
